const API_URL = import.meta.env.VITE_API_URL || 'https://expatsapi.nicarumba.com/api/v1';
const SITE_RESOLUTION_MODE = (import.meta.env.VITE_SITE_RESOLUTION_MODE ?? 'path').trim().toLowerCase();
const SITE_KEY = (import.meta.env.VITE_SITE_KEY ?? '').trim();

function pathSiteKey(): string {
  if (typeof window === 'undefined') return '';
  const m = window.location.pathname.match(/^\/s\/([^/]+)/);
  return m?.[1] ? decodeURIComponent(m[1]).trim() : '';
}

function sitePrefix(): string {
	if (SITE_RESOLUTION_MODE !== 'path') return '';
	const key = SITE_KEY || pathSiteKey() || 'default';
	return `/s/${encodeURIComponent(key)}`;
}

const REQUEST_TIMEOUT_MS = 30000;

let csrfToken: string | null = null;

/** Set by the auth layer (from /auth/me) so mutating requests can echo the token. */
export function setCsrfToken(token: string | null) {
  csrfToken = token;
}

function csrfHeaders(): Record<string, string> {
  return csrfToken ? { 'X-CSRF-Token': csrfToken } : {};
}

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const url = `${API_URL}${sitePrefix()}${path}`;

  // Enforce an overall timeout and honor any caller-supplied abort signal, so
  // in-flight requests can be cancelled (e.g. on component unmount / re-fetch).
  const controller = new AbortController();
  const callerSignal = options.signal ?? undefined;
  const onCallerAbort = () => controller.abort(callerSignal?.reason);
  if (callerSignal) {
    if (callerSignal.aborted) controller.abort(callerSignal.reason);
    else callerSignal.addEventListener('abort', onCallerAbort, { once: true });
  }
  const timeout = setTimeout(
    () => controller.abort(new DOMException('Request timed out', 'TimeoutError')),
    REQUEST_TIMEOUT_MS,
  );

  let res: Response;
  try {
    res = await fetch(url, {
      ...options,
      credentials: 'include',
      headers: { 'Content-Type': 'application/json', ...csrfHeaders(), ...options.headers },
      signal: controller.signal,
    });
  } catch (err) {
    // A deliberate caller cancellation surfaces as AbortError so effects can
    // ignore it; everything else is normalized to a typed ApiError.
    if (callerSignal?.aborted) throw err;
    if (err instanceof DOMException && err.name === 'TimeoutError') {
      throw new ApiError(0, 'Request timed out');
    }
    throw new ApiError(0, 'Network error');
  } finally {
    clearTimeout(timeout);
    callerSignal?.removeEventListener('abort', onCallerAbort);
  }

  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: res.statusText }));
    throw new ApiError(res.status, body?.error || 'Request failed');
  }
  if (res.status === 204) return {} as T;
  return res.json().catch(() => ({} as T));
}

export class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message);
    this.name = 'ApiError';
  }
}

export interface User {
  id: string;
  username: string;
  email: string;
  display_name: string;
  avatar: string;
  bio: string;
  website: string;
  location: string;
  notification_email: string;
  email_notifications: boolean;
  email_digest_frequency?: 'none' | 'daily' | 'weekly' | 'monthly';
  email_notification_delay_minutes?: number;
  last_digest_sent_at?: string;
  avatar_override: string;
  is_global_admin: boolean;
  email_verified: boolean;
  created_at: string;
  last_seen_at?: string;
}

export interface Category {
  id: string;
  site_id: string;
  parent_id?: string;
  name: string;
  slug: string;
  description: string;
  color: string;
  icon: string;
  position: number;
  is_qa_mode: boolean;
  topic_count: number;
  post_count: number;
}

export interface Topic {
  id: string;
  site_id: string;
  category_id: string;
  author_id: string;
  title: string;
  slug: string;
  legacy_path?: string;
  topic_type: string;
  needs_answer: boolean;
  is_pinned: boolean;
  is_locked: boolean;
  is_deleted: boolean;
  deleted_at?: string;
  deleted_by?: string;
  delete_reason?: string;
  view_count: number;
  reply_count: number;
  like_count: number;
  last_post_at?: string;
  accepted_post_id?: string;
  created_at: string;
  updated_at: string;
  author?: User;
  category?: Category;
  tags?: Tag[];
}

export interface Post {
  id: string;
  topic_id: string;
  topic_title?: string;
  topic_slug?: string;
  author_id: string;
  parent_id?: string;
  body: string;
  body_html: string;
  is_first_post: boolean;
  is_deleted: boolean;
  deleted_at?: string;
  deleted_by?: string;
  delete_reason?: string;
  like_count: number;
  edit_count: number;
  created_at: string;
  updated_at: string;
  author?: User;
}

export interface Tag {
  id: string;
  name: string;
  slug: string;
  color: string;
}

export interface Role {
  id: string;
  name: string;
  permissions: string[];
}

export interface ApiResponse<T> {
  data: T;
  meta?: { page: number; per_page: number; total_count: number; total_pages: number };
}

export interface MetaTags {
  keywords?: string;
  description?: string;
  og_image?: string;
  og_title?: string;
  twitter_card?: string;
  canonical_url?: string;
  extra_head?: string;
}

export interface EmailConfig {
  provider: string;
  host?: string;
  port?: number;
  username?: string;
  password?: string;
  from_addr?: string;
  from_name?: string;
  api_key?: string;
  enabled: boolean;
  notification_template_html?: string;
  digest_template_html?: string;
}

export interface Site {
  id: string;
  key: string;
  name: string;
  domain: string;
  frontend_url: string;
  description: string;
  logo_url: string;
  favicon_url: string;
  allow_signup: boolean;
  auth_mode: string;
  theme_settings: Record<string, string>;
  custom_css: string;
  turnstile_site_key?: string;
  force_email_verification: boolean;
  disable_turnstile: boolean;
  auto_hold_enabled: boolean;
  auto_hold_scope: 'topics' | 'all';
  auto_hold_threshold: number;
  is_private: boolean;
  disable_local_auth: boolean;
  email_verification_grandfathered_at?: string;
  email_config: EmailConfig;
  meta_tags: MetaTags;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface SiteUserDetail {
  user_id: string;
  username: string;
  email: string;
  display_name: string;
  avatar: string;
  register_ip?: string;
  role_name: string;
  role_id: string;
  joined_at: string;
  banned_at?: string;
  ban_reason?: string;
  last_seen_at?: string;
  post_count: number;
}

export interface AuditLogEntry {
  id: string;
  site_id?: string;
  actor_id?: string;
  action: string;
  entity: string;
  entity_id: string;
  details: string;
  ip_address?: string;
  created_at: string;
}

export interface Report {
  id: string;
  site_id: string;
  reporter_id: string;
  target_type: 'post' | 'topic';
  target_id: string;
  reason: string;
  status: 'open' | 'resolved' | 'dismissed';
  resolved_by?: string;
  resolved_at?: string;
  resolve_note?: string;
  created_at: string;
  reporter?: User;
  target_title?: string;
  target_body?: string;
}

export interface PendingSubmission {
  id: string;
  site_id: string;
  user_id: string;
  submission_type: 'topic' | 'post';
  status: 'pending' | 'approved' | 'denied';
  category_id?: string;
  topic_id?: string;
  parent_id?: string;
  title?: string;
  body: string;
  body_html: string;
  topic_type?: string;
  needs_answer: boolean;
  tag_ids?: string[];
  approved_topic_id?: string;
  approved_post_id?: string;
  decision_note?: string;
  decided_by?: string;
  decided_at?: string;
  created_at: string;
  updated_at: string;
  user?: User;
  topic_title?: string;
}

export type QueuedSubmissionResponse<T> = ApiResponse<T> | { pending: true; data: PendingSubmission; message: string };

export interface Notification {
  id: string;
  user_id: string;
  site_id: string;
  type: string;
  topic_id?: string;
  post_id?: string;
  actor_id?: string;
  message: string;
  topic_title?: string;
  is_read: boolean;
  read_at?: string;
  created_at: string;
  actor_name?: string;
  actor_avatar?: string;
  topic_slug?: string;
}

/** A site member offered by @mention autocomplete. */
export interface MentionUser {
  id: string;
  username: string;
  /** What to type after "@" to mention this member. */
  handle: string;
  display_name: string;
  avatar?: string;
}

export interface Follow {
  id: string;
  user_id: string;
  site_id: string;
  target_type: string;
  target_id: string;
  created_at: string;
}

export const api = {
  site: {
    info: () => request<ApiResponse<Site>>('/site'),
  },
  auth: {
    login: (email: string, password: string) =>
      request<ApiResponse<User>>('/auth/login', { method: 'POST', body: JSON.stringify({ email, password }) }),
    register: (data: { username: string; email: string; password: string; turnstile_token?: string; website?: string; form_started_at?: number }) =>
      request<ApiResponse<User>>('/auth/register', { method: 'POST', body: JSON.stringify(data) }),
    logout: () => request<void>('/auth/logout', { method: 'POST' }),
    me: () => request<ApiResponse<{ user: User | null; role?: Role; post_count?: number; csrf_token?: string; sso_reauth?: boolean }>>('/auth/me'),
    updateProfile: (data: Partial<User>) =>
      request<ApiResponse<User>>('/auth/profile', { method: 'PUT', body: JSON.stringify(data) }),
    uploadAvatar: (file: File) => {
      const form = new FormData();
      form.append('avatar', file);
      const url = `${API_URL}${sitePrefix()}/auth/avatar`;
      return fetch(url, { method: 'POST', credentials: 'include', headers: csrfHeaders(), body: form })
        .then(async (res) => {
          if (!res.ok) {
            const body = await res.json().catch(() => ({ error: res.statusText }));
            throw new ApiError(res.status, body.error || 'Upload failed');
          }
          return res.json() as Promise<ApiResponse<{ avatar_url: string; user: User }>>;
        });
    },
    changePassword: (old_password: string, new_password: string) =>
      request<void>('/auth/password', { method: 'PUT', body: JSON.stringify({ old_password, new_password }) }),
    requestPasswordReset: (email: string) =>
      request<ApiResponse<{ message: string }>>('/auth/password-reset/request', { method: 'POST', body: JSON.stringify({ email }) }),
    resetPassword: (token: string, new_password: string) =>
      request<ApiResponse<{ message: string }>>('/auth/password-reset/confirm', { method: 'POST', body: JSON.stringify({ token, new_password }) }),
    confirmEmailVerification: (token: string) =>
      request<ApiResponse<{ message: string }>>('/auth/email-verification/confirm', { method: 'POST', body: JSON.stringify({ token }) }),
    resendEmailVerification: (email: string) =>
      request<ApiResponse<{ message: string }>>('/auth/email-verification/resend', { method: 'POST', body: JSON.stringify({ email }) }),
    getUserProfile: (userID: string) => request<ApiResponse<{ user: User; stats?: any }>>(`/users/${userID}`),
    oidcConfig: () => request<{ enabled: boolean; provider_name?: string; disable_local_auth?: boolean }>(`/auth/oidc/config`),
    oidcLoginUrl: (opts?: { returnTo?: string; silent?: boolean }) => {
      const params = new URLSearchParams();
      if (opts?.returnTo) params.set('return_to', opts.returnTo);
      if (opts?.silent) params.set('silent', '1');
      const qs = params.toString();
      return `${API_URL}${sitePrefix()}/auth/oidc/login${qs ? `?${qs}` : ''}`;
    },
  },

  userContent: {
    listPosts: (userID: string, params?: Record<string, string>) => {
      const qs = params ? '?' + new URLSearchParams(params).toString() : '';
      return request<ApiResponse<Post[]>>(`/users/${userID}/posts${qs}`);
    },
  },
  categories: {
    list: () => request<ApiResponse<Category[]>>('/categories'),
    get: (slug: string) => request<ApiResponse<Category>>(`/categories/${slug}`),
  },
  topics: {
    list: (params?: Record<string, string>, signal?: AbortSignal) => {
      const qs = params ? '?' + new URLSearchParams(params).toString() : '';
      return request<ApiResponse<Topic[]>>(`/topics${qs}`, { signal });
    },
    get: (id: string, signal?: AbortSignal) => request<ApiResponse<Topic>>(`/topics/${id}`, { signal }),
    getByLegacyPath: (legacyPath: string) => request<ApiResponse<Topic>>(`/topic/${legacyPath.replace(/^\/+/, '')}`),
    create: (data: { category_id: string; title: string; body: string; body_html?: string; topic_type?: string; needs_answer?: boolean; tag_ids?: string[] }) =>
      request<QueuedSubmissionResponse<Topic>>('/topics', { method: 'POST', body: JSON.stringify(data) }),
    acceptAnswer: (topicId: string, postId: string) =>
      request<void>(`/topics/${topicId}/accept-answer`, { method: 'PUT', body: JSON.stringify({ post_id: postId }) }),
    update: (id: string, data: { title?: string; category_id?: string; topic_type?: string; needs_answer?: boolean }) =>
      request<ApiResponse<Topic>>(`/topics/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    updateTags: (id: string, tag_ids: string[]) =>
      request<void>(`/topics/${id}/tags`, { method: 'PUT', body: JSON.stringify({ tag_ids }) }),
    selfDelete: (id: string) => request<void>(`/topics/${id}/self`, { method: 'DELETE' }),
  },
  posts: {
    list: (topicID: string, params?: Record<string, string>, signal?: AbortSignal) => {
      const qs = params ? '?' + new URLSearchParams(params).toString() : '';
      return request<ApiResponse<Post[]>>(`/topics/${topicID}/posts${qs}`, { signal });
    },
    getPage: (topicID: string, postID: string, perPage: number) =>
      request<ApiResponse<{ page: number }>>(`/topics/${topicID}/posts/${postID}/page?per_page=${perPage}`),
    create: (topicId: string, data: { body: string; body_html?: string; parent_id?: string }) =>
      request<QueuedSubmissionResponse<Post>>(`/topics/${topicId}/posts`, { method: 'POST', body: JSON.stringify(data) }),
    update: (id: string, data: { body: string; body_html?: string }) =>
      request<ApiResponse<Post>>(`/posts/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    like: (id: string) => request<void>(`/posts/${id}/like`, { method: 'POST' }),
    unlike: (id: string) => request<void>(`/posts/${id}/like`, { method: 'DELETE' }),
    revisions: (id: string) => request<ApiResponse<any[]>>(`/posts/${id}/revisions`),
    selfDelete: (id: string) => request<void>(`/posts/${id}/self`, { method: 'DELETE' }),
  },
  tags: {
    list: () => request<ApiResponse<Tag[]>>('/tags'),
    get: (slug: string) => request<ApiResponse<Tag>>(`/tags/${slug}`),
  },
  search: (q: string, page = 1) =>
    request<ApiResponse<Topic[]>>(`/search?q=${encodeURIComponent(q)}&page=${page}`),
  follows: {
    follow: (target_type: string, target_id: string) =>
      request<void>('/follows', { method: 'POST', body: JSON.stringify({ target_type, target_id }) }),
    unfollow: (target_type: string, target_id: string) =>
      request<void>(`/follows?target_type=${target_type}&target_id=${target_id}`, { method: 'DELETE' }),
    check: (target_type: string, target_id: string) =>
      request<{ following: boolean }>(`/follows/check?target_type=${target_type}&target_id=${target_id}`),
    list: (target_type?: string) => {
      const qs = target_type ? `?target_type=${target_type}` : '';
      return request<ApiResponse<Follow[]>>(`/follows${qs}`);
    },
  },
  mentions: {
    searchUsers: (q: string, signal?: AbortSignal) =>
      request<ApiResponse<MentionUser[]>>(`/mentions/users?q=${encodeURIComponent(q)}`, { signal }),
  },
  notifications: {
    list: (params?: Record<string, string>) => {
      const qs = params ? '?' + new URLSearchParams(params).toString() : '';
      return request<ApiResponse<Notification[]>>(`/notifications${qs}`);
    },
    unreadCount: () => request<{ count: number }>('/notifications/unread-count'),
    markRead: (id: string) => request<void>(`/notifications/${id}/read`, { method: 'PUT' }),
    markAllRead: () => request<void>('/notifications/read-all', { method: 'PUT' }),
  },
  push: {
    vapidKey: () => request<{ public_key: string }>('/push/vapid-key'),
    subscribe: (subscription: { endpoint: string; p256dh: string; auth: string }) =>
      request<{ ok: boolean }>('/push/subscribe', { method: 'POST', body: JSON.stringify(subscription) }),
    unsubscribe: (endpoint: string) =>
      request<void>('/push/unsubscribe', { method: 'POST', body: JSON.stringify({ endpoint }) }),
  },
  reports: {
    create: (data: { target_type: string; target_id: string; reason: string }) =>
      request<ApiResponse<Report>>('/reports', { method: 'POST', body: JSON.stringify(data) }),
  },
  moderation: {
    pinTopic: (id: string, pinned: boolean) =>
      request<void>(`/mod/topics/${id}/pin`, { method: 'PUT', body: JSON.stringify({ pinned }) }),
    listReports: (params?: Record<string, string>) => {
      const qs = params ? '?' + new URLSearchParams(params).toString() : '';
      return request<ApiResponse<Report[]>>(`/mod/reports${qs}`);
    },
    countOpenReports: () => request<{ count: number }>('/mod/reports/count'),
    resolveReport: (id: string, data: { status: string; note?: string }) =>
      request<void>(`/mod/reports/${id}/resolve`, { method: 'PUT', body: JSON.stringify(data) }),
    listPending: (params?: Record<string, string>) => {
      const qs = params ? '?' + new URLSearchParams(params).toString() : '';
      return request<ApiResponse<PendingSubmission[]>>(`/mod/pending${qs}`);
    },
    countPending: () => request<{ count: number }>('/mod/pending/count'),
    approvePending: (id: string) => request<void>(`/mod/pending/${id}/approve`, { method: 'PUT' }),
    denyPending: (id: string, note?: string) =>
      request<void>(`/mod/pending/${id}/deny`, { method: 'PUT', body: JSON.stringify({ note: note || '' }) }),
    markPendingSpammer: (id: string) => request<void>(`/mod/pending/${id}/spammer`, { method: 'POST' }),
    listDeletedTopics: (params?: Record<string, string>) => {
      const qs = params ? '?' + new URLSearchParams(params).toString() : '';
      return request<ApiResponse<Topic[]>>(`/mod/deleted/topics${qs}`);
    },
    listDeletedPosts: (params?: Record<string, string>) => {
      const qs = params ? '?' + new URLSearchParams(params).toString() : '';
      return request<ApiResponse<Post[]>>(`/mod/deleted/posts${qs}`);
    },
    restoreTopic: (id: string) => request<void>(`/mod/topics/${id}/restore`, { method: 'PUT' }),
    restorePost: (id: string) => request<void>(`/mod/posts/${id}/restore`, { method: 'PUT' }),
    deleteTopic: (id: string, reason?: string) =>
      request<void>(`/mod/topics/${id}`, { method: 'DELETE', body: JSON.stringify({ reason: reason || '' }) }),
    deletePost: (id: string, reason?: string) =>
      request<void>(`/mod/posts/${id}`, { method: 'DELETE', body: JSON.stringify({ reason: reason || '' }) }),
  },
  mod: {
    uploadImage: (file: File) => {
      const form = new FormData();
      form.append('image', file);
      const url = `${API_URL}${sitePrefix()}/upload/image`;
      return fetch(url, { method: 'POST', credentials: 'include', headers: csrfHeaders(), body: form })
        .then(async (res) => {
          if (!res.ok) {
            const body = await res.json().catch(() => ({ error: res.statusText }));
            throw new ApiError(res.status, body.error || 'Upload failed');
          }
          return res.json() as Promise<ApiResponse<{ url: string }>>;
        });
    },
  },
  admin: {
    getSite: (siteID: string) =>
      request<ApiResponse<Site>>(`/admin/sites/${siteID}`),
    listSites: () =>
      request<ApiResponse<Site[]>>('/admin/sites'),
    updateSite: (siteID: string, data: Partial<Site>) =>
      request<ApiResponse<Site>>(`/admin/sites/${siteID}`, { method: 'PUT', body: JSON.stringify(data) }),
    grandfatherEmailVerification: (siteID: string, older_than_days = 7) =>
      request<ApiResponse<{ message: string; affected_users: number; older_than_days: number; site: Site }>>(
        `/admin/sites/${siteID}/email-verification/grandfather`,
        { method: 'POST', body: JSON.stringify({ older_than_days }) },
      ),
    listUsers: (params?: Record<string, string>) => {
      const qs = params ? '?' + new URLSearchParams(params).toString() : '';
      return request<ApiResponse<SiteUserDetail[]>>(`/admin/users${qs}`);
    },
    getUser: (userID: string) =>
      request<ApiResponse<SiteUserDetail>>(`/admin/users/${userID}`),
    listUserPosts: (userID: string, params?: Record<string, string>) => {
      const qs = params ? '?' + new URLSearchParams(params).toString() : '';
      return request<ApiResponse<Post[]>>(`/admin/users/${userID}/posts${qs}`);
    },
    listRoles: () =>
      request<ApiResponse<Role[]>>('/admin/roles'),
    pinTopic: (topicID: string, pinned: boolean) =>
      request<void>(`/admin/topics/${topicID}/pin`, { method: 'PUT', body: JSON.stringify({ pinned }) }),
    lockTopic: (topicID: string, locked: boolean) =>
      request<void>(`/admin/topics/${topicID}/lock`, { method: 'PUT', body: JSON.stringify({ locked }) }),
    setUserRole: (user_id: string, role_id: string) =>
      request<void>('/admin/users/role', { method: 'PUT', body: JSON.stringify({ user_id, role_id }) }),
    deleteUser: (userID: string, force = false) =>
      request<void>(`/admin/users/${userID}${force ? '?force=true' : ''}`, { method: 'DELETE' }),
    banUser: (userID: string, reason: string) =>
      request<void>(`/admin/users/${userID}/ban`, { method: 'POST', body: JSON.stringify({ reason }) }),
    unbanUser: (userID: string) =>
      request<void>(`/admin/users/${userID}/ban`, { method: 'DELETE' }),
    sendTestEmail: (email: string, type: 'notification' | 'digest' = 'notification') =>
      request<ApiResponse<{ message: string }>>('/admin/email/test', { method: 'POST', body: JSON.stringify({ email, type }) }),
    getAuditLog: (params?: Record<string, string>) => {
      const qs = params ? '?' + new URLSearchParams(params).toString() : '';
      return request<ApiResponse<AuditLogEntry[]>>(`/admin/audit-log${qs}`);
    },
    createCategory: (data: { name: string; slug: string; description?: string; color?: string }) =>
      request<ApiResponse<Category>>('/admin/categories', { method: 'POST', body: JSON.stringify(data) }),
    updateCategory: (id: string, data: Partial<Category>) =>
      request<ApiResponse<Category>>(`/admin/categories/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    deleteCategory: (id: string) =>
      request<void>(`/admin/categories/${id}`, { method: 'DELETE' }),
    createTag: (data: { name: string; slug: string; color?: string }) =>
      request<ApiResponse<Tag>>('/admin/tags', { method: 'POST', body: JSON.stringify(data) }),
    updateTag: (id: string, data: { name: string; color: string }) =>
      request<ApiResponse<Tag>>(`/admin/tags/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    deleteTag: (id: string) =>
      request<void>(`/admin/tags/${id}`, { method: 'DELETE' }),
  },
};
